"use client";

import { useState } from "react";

import { type BestQuickPicks } from "@/lib/api/best/quick-picks/schema";
import { cn } from "@/lib/utils";

import { getCategoriesFromData, type Category } from "../categories";
import { QuickPickCard } from "./quick-pick-card";

type Props = {
  quickPicksItems: BestQuickPicks;
};

export function QuickPicksContent({ quickPicksItems }: Props) {
  const categories = getCategoriesFromData(quickPicksItems);
  const [activeCategory, setActiveCategory] = useState<
    Category["id"] | undefined
  >(categories[0]?.id);

  if (!activeCategory) return null;

  const filteredItems = quickPicksItems.filter(
    (item) => item.category === activeCategory,
  );

  return (
    <div>
      <div className="flex flex-col gap-2 border-b border-border-grayscale-default py-2">
        <h3 className="h3 text-text-grayscale-title">
          Looking for the best? Start here
        </h3>
        <p className="body2 text-text-grayscale-caption">
          Based on what people keep buying, loving, and recommending. Less
          noise, more clarity
        </p>
      </div>

      <div className="flex gap-4 py-2 mb-3">
        {categories.map((category) => {
          const Icon = category.icon;
          const isActive = activeCategory === category.id;
          return (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={cn(
                "flex items-center gap-2 border-b-2 py-1 transition-colors duration-200 ease-out",
                isActive
                  ? "border-primary text-primary"
                  : "border-transparent text-text-grayscale-body hover:border-primary",
              )}
            >
              <Icon
                className={cn(
                  "size-5",
                  isActive ? "text-primary" : "text-text-grayscale-title",
                )}
              />
              <span className="mini1 font-medium">{category.label}</span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-3 gap-5">
        {filteredItems.slice(0, 3).map((item) => (
          <QuickPickCard
            key={`${item.category}:${item.id}`}
            item={item}
          />
        ))}
      </div>

      <p className="body2 text-text-grayscale-body mt-3 pb-4">
        *Quick Picks link directly to Amazon.
      </p>
    </div>
  );
}
